import { createRouter, createWebHistory, RouteRecordRaw, START_LOCATION } from "vue-router";
import { nextTick } from "vue";
import posthog from "posthog-js";
import { monitoringCaptureError } from "@/plugins/monitoring";

/*
 *Disabled till api migration for pipelines is done
 // import Pipelines from "../views/Pipelines.vue";
 // import PipelineDetail from "../views/PipelineDetail.vue";
 */

const routes: Array<RouteRecordRaw> = [
	{
		/*
		 *  Redirects to the default route.
		 */
		path: "/",
		redirect: "/home",
		beforeEnter: (to, from, next) => {
			/*
			 *  Check for history-fallback redirect.
			 *  Source: https://stackoverflow.com/a/61571528
			 */
			if (sessionStorage.getItem("redirect") != null) {
				const redirect = sessionStorage.redirect;
				delete sessionStorage.redirect;
				// Only allow root-relative paths to prevent open redirect (blocks //evil.com)
				if (
					typeof redirect === "string" &&
					redirect.startsWith("/") &&
					!redirect.startsWith("//") &&
					!redirect.includes("://")
				) {
					next({ path: redirect });
				} else {
					next("/home");
				}
			} else {
				next("/home");
			}
		},
	},
	{
		path: "/home",
		name: "Home",
		meta: {
			requiresAuth: true,
			title: "Home",
			prefetch: true,
		},
		component: () => import("@/views/Home.vue"),
	},
	{
		path: "/welcome",
		name: "Welcome",
		meta: {
			requiresAuth: true,
		},
		component: () => import("@/views/WelcomePage.vue"),
	},
	{
		path: "/launch",
		name: "CodeEditor",
		meta: {
			requiresAuth: true,
			title: "Code Playground",
		},
		component: () => import("@/views/DevelopmentPlatform.vue"),
	},
	{
		path: "/cores",
		name: "Cores",
		meta: {
			requiresAuth: true,
			title: "Cores",
			prefetch: true,
		},
		component: () => import("@/views/Cores.vue"),
	},
	{
		path: "/datasources",
		name: "DataSources",
		meta: {
			requiresAuth: true,
			title: "Data",
			prefetch: true,
		},
		component: () => import("@/views/DataSources.vue"),
	},
	{
		path: "/repositories",
		name: "Repositories",
		meta: {
			requiresAuth: true,
			title: "Repositories",
			prefetch: true,
		},
		component: () => import("@/views/Repositories.vue"),
	},
	{
		path: "/repositories/detail",
		name: "RepositoryDetail",
		meta: {
			requiresAuth: true,
			title: "Repository Detail",
			prefetch: true,
		},
		props: (route) => ({ repositoryID: route.query.repositoryID as string }),
		component: () => import("@/components/Repositories/RepositoryDetail.vue"),
	},
	{
		path: "/validate-performance",
		name: "ValidatePerformance",
		meta: {
			requiresAuth: true,
			title: "Validate Performance",
		},
		component: () => import(/* webpackChunkName: "benchmarks" */ "@/views/Benchmarks.vue"),
	},
	{
		path: "/validate-performance/:benchmarkID",
		name: "ValidatePerformanceRun",
		meta: {
			requiresAuth: true,
			title: "Benchmark Run",
		},
		props: true,
		component: () => import(/* webpackChunkName: "benchmarks" */ "@/views/BenchmarkRun.vue"),
	},
	{
		path: "/apps",
		name: "Apps",
		meta: {
			requiresAuth: true,
			title: "API App Highlights",
		},
		component: () => import("@/views/Apps.vue"),
	},
	/*
	  * Disabled till api migration for pipelines is done
	// {
	// 	path: "/pipelines",
	// 	name: "Pipelines",
	// 	meta: {
	// 		requiresAuth: true,
	// 	},
	// 	component: Pipelines,
	// },
	// {
	// 	path: "/pipelines/:pipeline",
	// 	name: "PipelineDetail",
	// 	meta: {
	// 		requiresAuth: true,
	// 	},
	// 	component: PipelineDetail,
	// },
	*/
	{
		path: "/tasks",
		name: "TaskManager",
		meta: {
			requiresAuth: true,
			title: "Builds and Tasks",
			prefetch: true,
		},
		component: () => import("@/views/TaskManager.vue"),
	},
	{
		path: "/tasks/:id",
		name: "TaskDetail",
		meta: {
			requiresAuth: true,
			title: "Task Detail",
			prefetch: true,
		},
		props: (route) => ({
			id: route.params.id,
			initialTaskData: route.params.initialTaskData,
		}),
		component: () => import("@/views/TaskDetail.vue"),
		beforeEnter: (from, to, next) => {
			return next();
		},
	},
	{
		path: "/usage",
		name: "Usage",
		meta: {
			requiresAuth: true,
			title: "Usage",
		},
		component: () => import("@/views/Usage.vue"),
	},
	{
		path: "/billing",
		meta: {
			requiresAuth: true,
			title: "Billing",
		},
		component: () => import("@/views/Billing.vue"),
		children: [
			{
				path: "",
				name: "Billing",
				component: () => import("@/components/BillingPanel.vue"),
			},
			{
				path: "subscribe",
				name: "Subscribe",
				meta: {
					requiresAuth: true,
				},
				component: () => import("@/components/Subscription.vue"),
			},
		],
	},
	{
		path: "/subscribe",
		redirect: "/billing",
	},
	{
		path: "/external/oauth/github/auth",
		name: "GithubAuth",
		meta: {
			requiresAuth: true,
		},
		component: () => import("@/views/GithubAuth.vue"),
		props: (route) => ({
			code: route.query.code,
			connect: route.query.connect,
			state: route.query.state,
			error: route.query.error,
			errorDescription: route.query.error_description,
		}),
	},
	{
		path: "/settings",
		meta: {
			requiresAuth: true,
			title: "Settings",
		},
		children: [
			{
				path: "",
				redirect: { name: "Settings_profile" },
			},
			{
				path: "profile",
				name: "Settings_profile",
				component: () => import("@/components/Settings/Profile.vue"),
			},
			{
				path: "general",
				name: "Settings_general",
				component: () => import("@/components/Settings/General.vue"),
			},
			{
				path: "account",
				redirect: { name: "Settings_profile" },
			},
			{
				path: "logs",
				name: "Settings_accountLogs",
				component: () => import("@/components/Settings/AccountLogs.vue"),
			},
			{
				path: "api",
				name: "Settings_cloudEngineAPI",
				component: () => import("@/components/Settings/ComputeEngineApi/CloudEngineAPI.vue"),
			},
			{
				path: "organizations",
				name: "Settings_organizations",
				component: () => import("@/components/Settings/Organization/Organizations.vue"),
			},
			// {
			// 	path: "billing",
			// 	name: "Settings_billing",
			// 	component: () => import(/* webpackChunkName: "settings" */ "@/components/Settings/Billing.vue"),
			// },
			// {
			// 	path: "integrations",
			// 	name: "Settings_integrations",
			// 	component: () => import(/* webpackChunkName: "settings" */ "@/components/Settings/Integrations.vue"),
			// },
			{
				path: ":pathMatch(.*)*",
				redirect: { name: "Settings_profile" },
			},
		],

		component: () => import("@/views/Settings.vue"),
	},
	{
		/*
		 *  Wildcard route that shows a 404 Not Found page.
		 */
		path: "/:pathMatch(.*)*",
		name: "NotFound",
		component: () => import("@/views/NotFound.vue"),
		meta: {
			title: "Page Not Found",
		},
	},
];

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes,
	scrollBehavior(_to, _from, savedPosition) {
		if (savedPosition) return savedPosition;
		return { top: 0 };
	},
});

/*
 * Setup router hooks
 */

//*  Send page view to analytics on load
router.afterEach((to) => {
	nextTick(() => {
		posthog?.capture("$pageview", {
			$current_url: to.fullPath,
		});
	});
});

//*  Reload the target when a deploy removed its chunk, except on first load where it would loop
router.onError((error, to, from) => {
	const isChunkLoadError =
		/Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/.test(
			error?.message,
		);
	if (isChunkLoadError && from !== START_LOCATION) {
		window.location.assign(to.fullPath);
		return;
	}
	monitoringCaptureError(error instanceof Error ? error : new Error(String(error)), "Route navigation");
});

export default router;
